// KPI Builder — "learners who have not completed", the dashboard's definition (GXACA-1028): registered minus completed.
// It copies facilityNonCompletionRate, whose backend handler takes the group field from the query, so any group that both
// the registration and the enrolment data have works the same way as facilities do.
import { SOURCES } from 'constants/kpiBuilderCatalogue';
import { filterFor, envelope, RecipeContext, RecipeResult } from './queries';
import { selectedGroups } from './lookups';
import { splitFilters } from './custom';
import { NOT_COMPLETED_ID } from './learnerGroups';
import { BuilderAnswers, EsNode, QueryObject, SourceId } from './types';

type EsClause = Record<string, unknown>;

/** Groups the registration data has as well, so a registered person can be placed in the same group as a completed one. */
const sharedGroupFields = (): string[] => SOURCES.reg.groups.map((g) => g.field as string);

const isCadre = (field?: string): boolean => field === 'profession';

/** True when the answers can be built with the dashboard definition: nothing is asked that the registration data cannot answer. */
export function usesRegisteredBase(a: BuilderAnswers, field: string): boolean {
  if (a.recipe !== 'rate' || a.rateSeg !== NOT_COMPLETED_ID || a.src !== 'enr') return false;
  const extraGroups = selectedGroups(a).some((g) => !isCadre(g.field));
  const typed = splitFilters(a.custom.filters);
  return sharedGroupFields().includes(field) && !extraGroups && typed.must.length === 0 && typed.mustNot.length === 0;
}

const BUCKETS = 'facilities';

function side(id: SourceId, agg: string, per: EsNode, c: RecipeContext, field: string): QueryObject {
  const named = [{ exists: { field } }, { bool: { must_not: [{ term: { [field]: '' } }] } }];
  const base = filterFor(id, c.a, true) as { bool?: { filter?: EsClause[] } };
  const filter = { bool: { filter: [...(base.bool?.filter ?? []), ...named] } };
  const path = `aggregations.${agg}.${BUCKETS}.buckets`;
  const value = id === 'reg' ? 'total_registered' : 'any_completed.unique_completed';
  const groups = { terms: { field, size: 200 }, aggs: per.aggs };
  return { ...envelope(id, c.a), filterPath: `${path}.key,${path}.${value}`, aggrQuery: { size: 0, aggs: { [agg]: { filter, aggs: { [BUCKETS]: groups } } } } };
}

/** Registered learners and learners with a completed enrolment, each counted per value of `field`; the handler turns them into a rate. */
export function registeredAndCompleted(c: RecipeContext, field: string, regId: SourceId, enrId: SourceId): RecipeResult {
  const registered: EsNode = { aggs: { total_registered: { cardinality: { field: 'userId' } } } };
  const completed: EsNode = { aggs: { any_completed: { filter: { terms: { enrollStatus: ['2'] } }, aggs: { unique_completed: { cardinality: { field: 'userId' } } } } } };
  return {
    queries: [side(regId, 'by_facility_registered', registered, c, field), side(enrId, 'by_facility_enrollment', completed, c, field)],
    cfg: { isDecimal: false, valueType: 'percentage', aggregationPaths: ['by_facility_registered', 'by_facility_enrollment'] },
    handlerKey: 'facilitynoncomplete',
  };
}

export const buildNoCompletion = (c: RecipeContext): RecipeResult => registeredAndCompleted(c, c.group.field as string, 'reg', 'enr');
